import React, { Dispatch } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgSelect, IgTextField } from "@intelligems/ig-theme";
import {
  StripeApiCreateCouponDto,
  StripeApiCreateCouponResponseBodyType,
} from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";
import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
} from "@mui/material";
import { usePostStripeApi } from "@src/api/stripe/stripe-api";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";

interface CreateCouponProps {
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
  onSuccess: () => void;
}

const defaultCoupon: StripeApiCreateCouponDto = {
  name: "",
  amountOff: null,
  duration: "once",
  durationInMonths: null,
  maxRedemptions: 1,
  percentOff: null,
  baseAmount: null,
};

export const CreateCoupon = ({
  payload,
  dispatchPayload,
  onSuccess,
}: CreateCouponProps) => {
  const createCoupon =
    usePostStripeApi<StripeApiCreateCouponResponseBodyType>();

  const [discountType, setDiscountType] = useImmer<"amountOff" | "percentOff">(
    "amountOff"
  );
  const [coupon, setCoupon] = useImmer<StripeApiCreateCouponDto>(defaultCoupon);

  const onCreate = () => {
    createCoupon.mutate(
      {
        action: "createCoupon",
        orgs: payload.orgs,
        coupon,
      },
      {
        onSuccess: (data) => {
          setCoupon(defaultCoupon);
          dispatchPayload({
            type: "setCoupon",
            value: data.coupon,
          });
          onSuccess();
        },
      }
    );
  };

  const isCreateDisabled = () => {
    if (!coupon.name) return true;

    if (!coupon.amountOff && !coupon.percentOff) return true;

    if (
      coupon.duration === "forever" &&
      discountType === "amountOff" &&
      (!coupon.baseAmount || !coupon.amountOff)
    ) {
      return true;
    }

    if (coupon.percentOff && coupon.percentOff > 100) return true;

    return false;
  };

  return (
    <Grid container item direction={"column"} rowSpacing={4}>
      <Grid
        container
        item
        flex={1}
        justifyContent={"space-between"}
        columnSpacing={2}
      >
        <Grid item sx={{ width: "395px" }}>
          <IgTextField
            label={"Coupon Name"}
            disabled={createCoupon.isPending}
            value={coupon.name}
            inputProps={{ maxLength: 39 }}
            onChange={(e) =>
              setCoupon((draft) => {
                draft.name = e.target.value;
                return draft;
              })
            }
          />
        </Grid>
      </Grid>
      <Grid container item columnSpacing={2}>
        <Grid item>
          <IgSelect
            disabled={createCoupon.isPending}
            sx={{ width: "220px" }}
            fullWidth={true}
            value={discountType || 0}
            label={"Discount Type"}
            onChange={(e) => {
              setDiscountType(e.target.value as any);
              if (e.target.value === "percentOff") {
                setCoupon((draft) => {
                  draft.amountOff = null;
                });
              } else {
                setCoupon((draft) => {
                  draft.percentOff = null;
                });
              }
            }}
          >
            <MenuItem value={"percentOff"}>Percentage Discount</MenuItem>
            <MenuItem value={"amountOff"}>Fixed Amount Discount</MenuItem>
          </IgSelect>
        </Grid>
        <Grid item>
          {discountType === "amountOff" ? (
            <Grid container item columnSpacing={2}>
              <Grid item mt={2}>
                <IgTextField
                  disabled={createCoupon.isPending}
                  label={"Amount Off"}
                  type={"number"}
                  sx={{ width: "150px" }}
                  value={coupon.amountOff}
                  onChange={(e) =>
                    setCoupon((draft) => {
                      draft.amountOff = ensureNumber(e.target.value);
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {coupon.duration === "forever" ? (
                <Grid item mt={2}>
                  <IgTextField
                    disabled={createCoupon.isPending}
                    label={"From Amount"}
                    type={"number"}
                    sx={{ width: "150px" }}
                    value={coupon.baseAmount}
                    onChange={(e) =>
                      setCoupon((draft) => {
                        draft.baseAmount = ensureNumber(e.target.value);
                      })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : (
            <IgTextField
              disabled={createCoupon.isPending}
              error={Boolean(coupon.percentOff && coupon.percentOff > 100)}
              label={"Percentage Off"}
              type={"number"}
              sx={{ width: "150px" }}
              value={coupon.percentOff}
              onChange={(e) =>
                setCoupon((draft) => {
                  draft.percentOff = ensureNumber(e.target.value);
                })
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          )}
        </Grid>
      </Grid>
      <Grid container item columnSpacing={2}>
        <Grid item>
          <IgSelect
            disabled={createCoupon.isPending}
            sx={{ width: "220px" }}
            value={coupon.duration}
            label={"Duration"}
            onChange={(e) => {
              setCoupon((draft) => {
                draft.duration = e.target.value;

                if (draft.duration !== "repeating") {
                  draft.durationInMonths = null;
                }
                return draft;
              });
            }}
          >
            <MenuItem value={"forever"}>Forever</MenuItem>
            <MenuItem value={"once"}>Once</MenuItem>
            <MenuItem value={"repeating"}>Multiple Months</MenuItem>
          </IgSelect>
        </Grid>
        <Grid item>
          {coupon.duration === "repeating" && (
            <IgTextField
              disabled={createCoupon.isPending}
              sx={{ width: "150px" }}
              label={"Number of Months"}
              type={"number"}
              value={coupon.durationInMonths}
              onChange={(e) =>
                setCoupon((draft) => {
                  draft.durationInMonths = ensureNumber(e.target.value);
                })
              }
            />
          )}
        </Grid>
      </Grid>
      <Grid container item columnSpacing={2}>
        <Grid item>
          <IgTextField
            disabled={createCoupon.isPending}
            label={"Max Redemptions"}
            type={"number"}
            sx={{ width: "220px" }}
            value={coupon.maxRedemptions}
            onChange={(e) =>
              setCoupon((draft) => {
                draft.maxRedemptions = ensureNumber(e.target.value);
              })
            }
          />
        </Grid>
        <Grid item mt={5}>
          <IgButton
            loading={createCoupon.isPending}
            onClick={onCreate}
            disabled={isCreateDisabled()}
          >
            Create
          </IgButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
